import { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, LineChart, Line,
} from 'recharts';
import {
  ShieldCheck, AlertTriangle, Activity, TrendingUp, RefreshCw, Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { apiFetch } from '../lib/supabase';
import {
  CHART_PRIMARY, CHART_SECONDARY, CHART_ERROR,
  PMS_COOL_GRAY_3C, PMS_COOL_GRAY_11C, BRAND_PRIMARY, ACCENT,
} from '../lib/palette';
import { assessedElementComplianceDetail } from '../lib/projectAuditHistory';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

/**
 * Custom recharts tooltip for the audit-over-time sparkline.
 * Mar 2, 2026 — Improvement A. Replaces the default tooltip so reviewers
 * see the audit's date, filename, element count, and pass rate in a
 * compact card. Reads from ``payload[0].payload`` — the per-point data
 * object built in ``sparkData``. Inline styles only (no new CSS).
 */
function SparklineTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${PMS_COOL_GRAY_3C}`,
        borderRadius: 4,
        padding: '8px 10px',
        fontSize: 12,
        minWidth: 160,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
    >
      <div style={{ color: PMS_COOL_GRAY_11C, fontSize: 11, marginBottom: 2 }}>
        {p.date}
      </div>
      <div style={{ fontWeight: 600, color: '#111', marginBottom: 4 }}>
        {p.label}
      </div>
      {p.total_elements ? (
        <div style={{ color: '#111' }}>
          Elements: <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {p.total_elements.toLocaleString()}
          </span>
        </div>
      ) : null}
      <div style={{ color: '#111' }}>
        Pass rate:{' '}
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {isFiniteNumber(p.pass_rate) ? `${p.pass_rate.toFixed(2)}%` : 'Unavailable'}
        </span>
      </div>
    </div>
  );
}

/**
 * Sprint 4 — Project Dashboard Charts.
 *
 * Aggregates statistics across EVERY completed audit of one project.
 * Three widgets, all using the Pantone Solid Coated palette (mirrors
 * the backend ``services/palette.py`` so PDF and UI charts match):
 *
 *   • KPI strip  — completed audits, element compliance (weighted, QG_*
 *                   excluded, latest audit per file), failing elements,
 *                   failing rule families, critical failures, active rules.
 *   • Bar chart  — per-category PASS / WARN / FAIL counts. Stacked bars
 *                   so reviewers see proportions at a glance.
 *   • Donut      — severity histogram (critical / major / minor).
 *   • Sparkline  — pass-rate trend across the project's audit history.
 *
 * Props:
 *   projectId — UUID or code of the project to load stats for.
 */
export default function ProjectDashboardCharts({ projectId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);

  const fetchStats = useMemo(() => async () => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      const resp = await apiFetch(`${API_URL}/api/projects/${projectId}/dashboard-stats`);
      if (!resp.ok) {
        const t = await resp.text().catch(() => '');
        throw new Error(`Could not load stats (${resp.status}): ${t.slice(0, 200)}`);
      }
      setStats(await resp.json());
    } catch (e) {
      setError(e.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) {
    return (
      <div className="card-elevated p-8 flex items-center justify-center text-muted-foreground" data-testid="dashboard-charts-loading">
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Loading project dashboard…
      </div>
    );
  }
  if (error) {
    return (
      <div className="card-elevated p-4 border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/5 text-sm text-[hsl(var(--destructive))]" data-testid="dashboard-charts-error">
        {error}
      </div>
    );
  }
  if (!stats || stats.completed_audit_count === 0) {
    return (
      <div className="card-elevated p-6 text-center text-muted-foreground" data-testid="dashboard-charts-empty">
        <Activity className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
        No completed audits yet. Run an audit to populate the dashboard.
      </div>
    );
  }

  const kpis = stats.kpis || {};
  const ec = kpis.element_compliance;
  const flaggedRules = (stats.rule_health || []).filter((r) => r.flagged);
  // Category bar chart data — pre-trimmed to the top 8 categories so the
  // chart stays legible. Backend already sorts by (fail + warn) desc.
  // P02 Batch 5: categories with < 30 checks carry an "(n=X)" low-sample
  // annotation so small populations aren't over-read.
  const catData = (stats.category_breakdown || [])
    .filter((category) => !/^(META|QG_)/i.test(category.category || ''))
    .slice(0, 8).map((c) => ({
    name: c.total < 30
      ? `${shortCategoryLabel(c.category)} (n=${c.total})`
      : shortCategoryLabel(c.category),
    pass: c.pass,
    warn: c.warn,
    fail: c.fail,
    pass_rate: c.pass_rate,
  }));

  // Sparkline — only one point per audit; we show pass_rate as a line so
  // the reviewer sees whether handover quality is trending up or down.
  const sparkData = (stats.audit_over_time || []).map((p, i) => ({
    idx: i + 1,
    label: p.ifc_file_name || `Audit ${i + 1}`,
    date: p.completed_at ? new Date(p.completed_at).toLocaleDateString() : '',
    pass_rate: p.pass_rate,
    fail_count: p.fail_count,
    total_elements: p.total_elements ?? null,
    duration_seconds: p.audit_duration_seconds ?? 0,
  }));

  return (
    <div className="space-y-4" data-testid="project-dashboard-charts">
      {/* Header strip with refresh */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
          <Activity className="h-4 w-4 text-[hsl(var(--accent-foreground))]" />
          Project Dashboard
          <span className="text-xs text-muted-foreground font-normal">
            · {stats.completed_audit_count} completed audit{stats.completed_audit_count !== 1 ? 's' : ''}
          </span>
        </h2>
        <Button size="sm" variant="outline" onClick={fetchStats} data-testid="dashboard-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5 mr-1" />
          Refresh
        </Button>
      </div>

      {/* ── KPI strip — P02 Batch 5 element-weighted metrics ────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dashboard-kpis">
        <KPI label="Audits" value={stats.completed_audit_count} testid="kpi-audits" />
        <KPI
          label="Assessed-element compliance"
          value={isFiniteNumber(ec?.percent) ? `${ec.percent}%` : 'Unavailable'}
          sub={assessedElementComplianceDetail(ec?.compliant_elements, ec?.checked_elements)}
          testid="kpi-element-compliance"
          tone="success"
          title="Of elements a BEP rule actually ran on, the share with no failure. Elements no rule applied to are not counted."
        />
        <KPI
          label="Failing elements"
          value={kpis.failing_elements != null ? kpis.failing_elements.toLocaleString() : '—'}
          sub="≥1 custom-rule FAIL"
          testid="kpi-failing-elements"
          tone="fail"
        />
        <KPI
          label="Distinct failing rules"
          value={kpis.failing_rule_families != null ? kpis.failing_rule_families : '—'}
          sub="Distinct rules with ≥1 FAIL"
          testid="kpi-failing-rule-families"
          tone="warn"
          title="Distinct rule IDs with at least one failure."
        />
        <KPI label="Active rules" value={kpis.active_rules != null ? kpis.active_rules : 'Unavailable'} testid="kpi-active-rules" />
      </div>
      <p className="text-[10px] text-muted-foreground" data-testid="kpi-scope-note">
        Latest-file scope · one latest completed audit per IFC file · QG and META supplements excluded from compliance KPIs
      </p>

      {/* ── Rule health — amber "Review rule" list (P02 Batch 5) ────── */}
      {flaggedRules.length > 0 && (
        <div className="card-elevated p-4 border border-amber-300/70 bg-amber-50/60" data-testid="rule-health-panel">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-1">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            Rule health — {flaggedRules.length} rule{flaggedRules.length !== 1 ? 's' : ''} to review
          </h3>
          <p className="text-xs text-muted-foreground mb-2">
            Failing on &gt;90% of ≥20 checks in the latest audits — the rule itself may be miscalibrated for these models.
          </p>
          <div className="space-y-1">
            {flaggedRules.map((r) => (
              <div key={r.rule_id} className="flex items-center gap-2 text-xs py-1" data-testid={`rule-health-flag-${r.rule_id}`}>
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 font-semibold text-[10px] uppercase tracking-wide whitespace-nowrap">
                  Review rule
                </span>
                <span className="font-medium text-foreground">{r.rule_name || r.rule_id}</span>
                <span className="ml-auto text-muted-foreground tabular-nums whitespace-nowrap">
                  {(r.fail_fraction * 100).toFixed(0)}% fail · n={r.pop.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Historical category chart ───────────────────────────────── */}
      <div>
        <Card data-testid="category-breakdown-chart">
          <CardHeader>
            <CardTitle
              className="text-sm font-semibold flex items-center gap-2"
              title="All completed audits of this project, including superseded runs."
            >
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--success))]" />
              Historical category breakdown
              <span className="text-xs font-normal text-muted-foreground">
                Historical scope · stacked PASS / WARN / FAIL across completed audits
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {catData.length === 0 ? (
              <p className="text-sm text-muted-foreground italic py-8 text-center">
                No category data yet.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={260} debounce={100}>
                <BarChart data={catData} margin={{ top: 8, right: 8, left: 0, bottom: 36 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={PMS_COOL_GRAY_3C} vertical={false} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: PMS_COOL_GRAY_11C }}
                    angle={-25} textAnchor="end" height={50} interval={0}
                  />
                  <YAxis tick={{ fontSize: 10, fill: PMS_COOL_GRAY_11C }} />
                  <Tooltip
                    contentStyle={{
                      fontSize: 12,
                      borderColor: PMS_COOL_GRAY_3C,
                      borderRadius: 4,
                    }}
                    cursor={{ fill: PMS_COOL_GRAY_3C, fillOpacity: 0.2 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="pass" name="PASS" stackId="checks" fill={CHART_SECONDARY} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="warn" name="WARN" stackId="checks" fill={CHART_PRIMARY} />
                  <Bar dataKey="fail" name="FAIL" stackId="checks" fill={CHART_ERROR} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Sparkline — audit-over-time pass rate ───────────────────── */}
      <Card data-testid="audit-over-time-chart">
        <CardHeader>
          <CardTitle
            className="text-sm font-semibold flex items-center gap-2"
            title="One point per audit, all files mixed. Pass-rate definition changed 2026-09-03 (Not Assessed excluded from the denominator); earlier points use the old definition."
          >
            <TrendingUp className="h-4 w-4 text-[hsl(var(--info))]" />
            Historical applicable-check pass rate
            <span className="text-xs font-normal text-muted-foreground">
              Historical scope · {sparkData.length} audit{sparkData.length !== 1 ? 's' : ''}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sparkData.length < 2 ? (
            <p className="text-sm text-muted-foreground italic py-6 text-center">
              Run at least 2 audits to see a trend.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={150} debounce={100}>
              <LineChart data={sparkData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={PMS_COOL_GRAY_3C} vertical={false} />
                <XAxis
                  dataKey="idx"
                  tick={{ fontSize: 10, fill: PMS_COOL_GRAY_11C }}
                  label={{ value: 'Audit #', position: 'insideBottom', offset: -2, fontSize: 10, fill: PMS_COOL_GRAY_11C }}
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 10, fill: PMS_COOL_GRAY_11C }}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip content={<SparklineTooltip />} />
                <Line
                  type="monotone"
                  dataKey="pass_rate"
                  stroke={CHART_PRIMARY}
                  strokeWidth={2}
                  dot={{ r: 4, fill: ACCENT, stroke: BRAND_PRIMARY, strokeWidth: 1 }}
                  activeDot={{ r: 6, fill: ACCENT }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* ── Drop-history table — Improvement C, Mar 2, 2026 ─────────
          Per-audit row table that lets reviewers spot when a specific
          model drop regressed pass rate or shrunk in element count.
          Newest first. ``[...sparkData].reverse()`` clones so the
          underlying ``sparkData`` (oldest-first for the LineChart) is
          not mutated. Pass-rate cell traffic-lights at 90 / 70 cutoffs.
          Only renders when there is at least one completed audit so
          the section disappears cleanly on fresh projects. */}
      {sparkData.length > 0 && (
        <div className="card-elevated p-4" data-testid="model-drop-history">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-[hsl(var(--accent-foreground))]" />
            Historical model drop history
            <span className="text-xs font-normal text-muted-foreground">
              Historical scope · {sparkData.length} audit{sparkData.length !== 1 ? 's' : ''}
            </span>
          </h3>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '220px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#111', zIndex: 2, boxShadow: '0 1px 0 rgba(0,0,0,0.15)' }}>
                <tr>
                  <th style={{ textAlign: 'left',  padding: '8px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111', borderBottom: `1px solid #000` }}>Date</th>
                  <th style={{ textAlign: 'left',  padding: '8px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111', borderBottom: `1px solid #000` }}>IFC File (full prefix)</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111', borderBottom: `1px solid #000` }} title="Elements.">Elements</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111', borderBottom: `1px solid #000` }}>Pass rate</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111', borderBottom: `1px solid #000` }}>Failures</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', color: '#fff', fontWeight: 700, backgroundColor: '#111', borderBottom: `1px solid #000` }}>Duration</th>
                </tr>
              </thead>
              <tbody>
                {[...sparkData].reverse().map((p, i) => {
                  const pr = isFiniteNumber(p.pass_rate) ? p.pass_rate : null;
                  const prColor = pr === null ? PMS_COOL_GRAY_11C : pr >= 90 ? '#15803d' : pr >= 70 ? '#b45309' : '#b91c1c';
                  return (
                    <tr
                      key={`drop-${p.idx}`}
                      data-testid={`drop-row-${i}`}
                      style={{ backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.03)' }}
                    >
                      <td style={{ padding: '6px 8px', color: '#111' }}>{p.date || '—'}</td>
                      <td style={{ padding: '6px 8px', color: '#111', fontVariantNumeric: 'tabular-nums' }}>{p.label}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: '#111', fontVariantNumeric: 'tabular-nums' }}>
                        {p.total_elements != null ? p.total_elements.toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: prColor, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {pr === null ? 'Unavailable' : `${pr.toFixed(2)}%`}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: '#111', fontVariantNumeric: 'tabular-nums' }}>
                        {isFiniteNumber(p.fail_count) ? p.fail_count.toLocaleString() : 'Unavailable'}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: '#111', fontVariantNumeric: 'tabular-nums' }}>
                        {p.duration_seconds > 0
                          ? p.duration_seconds < 60
                            ? `${Math.round(p.duration_seconds)}s`
                            : `${Math.round(p.duration_seconds / 60)}m`
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Latest-file views ───────────────────────────────────────── */}
      {stats.per_file_breakdown && stats.per_file_breakdown.length > 0 && (
        <>
          <FederatedComplianceMatrix perFile={stats.per_file_breakdown} />
          <ElementCoverageChart perFile={stats.per_file_breakdown} />
        </>
      )}
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────────────── */

/* ── Per-file federated visualisations — Mar 2, 2026 ──────────────
   All five components are pure presentational, driven by the new
   ``stats.per_file_breakdown`` payload from
   GET /api/projects/{id}/dashboard-stats. They render side-by-side
   to give a BIM manager a single-glance picture of regression risk
   and discipline-level compliance across federated model drops.
*/
function FederatedComplianceMatrix({ perFile }) {
  if (!perFile || perFile.length === 0) return null;

  const allCats = [...new Set(
    perFile.flatMap(f => Object.keys(f.category_rates || {}))
  )].filter((category) => category && !/^(META|QG_)/i.test(category));

  const catLabel = (c) => ({
    custom_bep_rules: 'BEP Rules',
    naming_compliance: 'Naming',
    fm_parameters: 'FM Params',
    geometry_completeness: 'Geometry',
    ifc_schema: 'IFC Schema',
    cobie_handover: 'COBie',
    spatial_containment: 'Spatial',
    baseline_ifc: 'Baseline',
    jurisdiction: 'Jurisdiction',
    fm_baseline: 'FM Baseline',
  }[c] || c);

  const cellColor = (rate) => {
    if (rate === null || rate === undefined) return '#f3f4f6';
    if (rate >= 90) return '#dcfce7';
    if (rate >= 70) return '#fef9c3';
    if (rate >= 50) return '#fed7aa';
    return '#fee2e2';
  };
  const cellText = (rate) => (rate === null || rate === undefined ? '—' : `${rate.toFixed(0)}%`);
  const textColor = (rate) => {
    if (rate === null || rate === undefined) return '#9ca3af';
    if (rate >= 90) return '#166534';
    if (rate >= 70) return '#854d0e';
    if (rate >= 50) return '#9a3412';
    return '#991b1b';
  };

  const discColor = {
    Architecture: '#3B6D11', Structure: '#185FA5',
    MEP: '#BA7517', Electrical: '#7B1FA2', Unknown: '#888780',
  };

  return (
    <div className="card-elevated p-4" data-testid="federated-compliance-matrix">
      <h3 className="text-sm font-semibold text-foreground mb-1">
        Latest-file compliance matrix
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        Latest-file scope · latest completed audit per full IFC filename · META and QG supplements excluded
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left',  padding: '6px 8px', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb', minWidth: 100 }}>File</th>
              <th style={{ textAlign: 'left',  padding: '6px 8px', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb', minWidth: 80 }}>Discipline</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb', minWidth: 70 }} title="Elements.">Overall</th>
              {allCats.map(cat => (
                <th key={cat} style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600, color: '#374151', borderBottom: '2px solid #e5e7eb', minWidth: 72 }}
                    title={(cat === 'META' || cat === 'custom_bep_rules') ? 'Checks.' : undefined}>
                  {catLabel(cat)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {perFile.map((f, i) => (
              <tr key={f.audit_id} style={{ backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)' }}>
                <td style={{ padding: '7px 8px', fontWeight: 600, color: '#111', fontVariantNumeric: 'tabular-nums' }}>{f.ifc_file_name || f.suffix}</td>
                <td style={{ padding: '7px 8px' }}>
                  <span style={{ color: discColor[f.discipline] || '#888', fontWeight: 600, fontSize: 10, letterSpacing: '0.04em' }}>
                    {f.discipline}
                  </span>
                </td>
                <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  color: !isFiniteNumber(f.pass_rate) ? '#9ca3af' : f.pass_rate >= 90 ? '#166534' : f.pass_rate >= 70 ? '#854d0e' : '#991b1b' }}>
                  {isFiniteNumber(f.pass_rate) ? `${f.pass_rate.toFixed(2)}%` : 'Unavailable'}
                </td>
                {allCats.map(cat => {
                  const rate = f.category_rates?.[cat];
                  return (
                    <td key={cat} style={{ padding: '7px 8px', textAlign: 'center',
                      backgroundColor: cellColor(rate), color: textColor(rate),
                      fontWeight: 600, fontVariantNumeric: 'tabular-nums', borderLeft: '1px solid #f3f4f6' }}>
                      {cellText(rate)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ElementCoverageChart({ perFile }) {
  if (!perFile || perFile.length === 0) return null;
  const data = perFile.map(f => ({
    name: f.ifc_file_name || f.suffix,
    total: f.total_elements,
    discipline: f.discipline,
  })).filter(d => d.total > 0);
  if (data.length === 0) return null;

  return (
    <div className="card-elevated p-4" data-testid="element-coverage-chart">
      <h3 className="text-sm font-semibold text-foreground mb-1">Element volume by file</h3>
      <p className="text-xs text-muted-foreground mb-3">
        Latest-file scope · total IFC elements only
      </p>
      <ResponsiveContainer width="100%" height={180} debounce={100}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 40, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 10, fill: '#6b7280' }}
            tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#374151', fontWeight: 600 }} width={72} />
          <Tooltip formatter={(v) => [v.toLocaleString(), 'Total elements']}
            contentStyle={{ fontSize: 12, borderColor: '#e5e7eb', borderRadius: 4 }} />
          <Bar dataKey="total" name="total elements" fill={PMS_COOL_GRAY_11C} radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function KPI({ label, value, testid, tone, sub, title = '' }) {
  // Light tone variants: success = green text, fail = red, warn = amber.
  const toneClass = {
    success: 'text-[hsl(var(--success))]',
    fail:    'text-[hsl(var(--destructive))]',
    warn:    'text-[hsl(var(--warning))]',
  }[tone] || 'text-foreground';
  return (
    <div className="p-3 rounded-md border border-border bg-card" data-testid={testid} title={title || undefined}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`text-xl font-semibold ${toneClass}`}>{value}</p>
      {sub ? <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p> : null}
    </div>
  );
}

// Shorten the snake_case category names to a human label that fits inside
// the bar-chart tick area.
function shortCategoryLabel(cat) {
  const map = {
    custom_bep_rules:       'Custom BEP',
    fm_parameters:          'FM params',
    naming_compliance:      'Naming',
    geometry_completeness:  'Geometry',
    ifc_schema:             'IFC schema',
    cobie_handover:         'COBie',
    spatial_containment:    'Spatial',
    classification:         'Classification',
    uncategorised:          'Other',
  };
  return map[cat] || cat;
}
